import { useCallback, useContext } from 'react'

import ProjectsDataContext from '@ors/contexts/Projects/ProjectsDataContext'
import { SectionTitle } from '../ProjectsCreate/ProjectsCreate'
import {
  booleanDetailItem,
  dateDetailItem,
  detailItem,
  numberDetailItem,
} from './ViewHelperComponents'
import { OptionsType, ProjectTypeApi } from '../interfaces'
import { canViewField, hasFields } from '../utils'
import {
  considerationOpts,
  tableColumns,
  viewColumnsClassName,
} from '../constants'
import { useStore } from '@ors/store'

import { Divider } from '@mui/material'
import { find, map } from 'lodash'

const ProjectCrossCutting = ({
  project,
  fieldHistory,
}: {
  project: ProjectTypeApi
  fieldHistory?: any
}) => {
  const { projectFields, viewableFields } = useStore(
    (state) => state.projectFields,
  )
  const { consumptionLevelStatuses } = useContext(ProjectsDataContext)

  const isApproved = project.submission_status === 'Approved'

  const canViewAboutSection =
    canViewField(viewableFields, 'title') ||
    canViewField(viewableFields, 'description')
  const canViewDetailsSection = hasFields(
    projectFields,
    viewableFields,
    'Cross-Cutting',
    false,
    ['title', 'description'],
  )

  const consideration = considerationOpts.find(
    (opt) => opt.value === project.blanket_or_individual_consideration,
  )
  const consumption_level_status = find(consumptionLevelStatuses, {
    id: project.consumption_level_status,
  }) as OptionsType

  const subsectors =
    project.subsectors?.length > 0
      ? map(project.subsectors, 'name').join(', ')
      : '-'

  const getFieldHistory = useCallback(
    (name: string) => {
      return fieldHistory?.[name] ?? []
    },
    [fieldHistory],
  )

  const formattedHistoryLvc = map(
    getFieldHistory('consumption_level_status'),
    (history) => ({
      ...history,
      value: find(consumptionLevelStatuses, { id: history.value })?.name,
    }),
  )

  return (
    <>
      {canViewAboutSection && (
        <>
          <SectionTitle>About</SectionTitle>
          <div className="flex w-full flex-col gap-4">
            {canViewField(viewableFields, 'title') &&
              detailItem(tableColumns.title, project.title, {
                fieldHistory: getFieldHistory('title'),
              })}
            {canViewField(viewableFields, 'description') &&
              detailItem(tableColumns.description, project.description, {
                detailClassname: 'self-start',
                fieldHistory: getFieldHistory('description'),
              })}
          </div>
        </>
      )}

      {canViewAboutSection && canViewDetailsSection && (
        <Divider className="my-6" />
      )}

      {canViewDetailsSection && (
        <>
          <SectionTitle>Details</SectionTitle>

          <div className="flex flex-col gap-4">
            <div className="flex w-full flex-col gap-4">
              <div className={viewColumnsClassName}>
                {canViewField(viewableFields, 'project_type') &&
                  detailItem(tableColumns.type, project.project_type?.name, {
                    fieldHistory: getFieldHistory('project_type'),
                  })}
                {canViewField(viewableFields, 'sector') &&
                  detailItem(tableColumns.sector, project.sector?.name, {
                    fieldHistory: getFieldHistory('sector'),
                  })}
                {canViewField(viewableFields, 'subsectors') &&
                  detailItem(tableColumns.subsectors, subsectors, {
                    fieldHistory: getFieldHistory('subsectors'),
                  })}
                {canViewField(viewableFields, 'consumption_level_status') &&
                  detailItem(
                    tableColumns.consumption_level_status,
                    consumption_level_status?.name,
                    {
                      fieldHistory: formattedHistoryLvc,
                    },
                  )}
              </div>
            </div>
            <div className="flex w-full flex-col gap-4">
              <div className={viewColumnsClassName}>
                {canViewField(viewableFields, 'total_fund') &&
                  numberDetailItem(
                    tableColumns.total_fund + ' (US $)',
                    project.total_fund as string,
                    'decimal',
                    getFieldHistory('total_fund'),
                  )}
                {canViewField(viewableFields, 'adjustment') &&
                  isApproved &&
                  booleanDetailItem(
                    tableColumns.adjustment,
                    project.adjustment ?? false,
                    getFieldHistory('adjustment'),
                  )}
                {canViewField(viewableFields, 'support_cost_psc') &&
                  numberDetailItem(
                    tableColumns.support_cost_psc + ' (US $)',
                    project.support_cost_psc as string,
                    'decimal',
                    getFieldHistory('support_cost_psc'),
                  )}
                {canViewField(viewableFields, 'interest') &&
                  isApproved &&
                  numberDetailItem(
                    tableColumns.interest,
                    project.interest as string,
                    'decimal',
                    getFieldHistory('interest'),
                  )}
              </div>
            </div>
            {project.status === 'Transferred' && (
              <div className="flex w-full flex-col gap-4">
                <div className={viewColumnsClassName}>
                  {numberDetailItem(
                    tableColumns.fund_transferred,
                    project.fund_transferred?.toString(),
                    'decimal',
                  )}
                  {numberDetailItem(
                    tableColumns.psc_transferred,
                    project.psc_transferred?.toString(),
                    'decimal',
                  )}
                </div>
              </div>
            )}
            <div className="flex w-full flex-col gap-4">
              <div className={viewColumnsClassName}>
                {canViewField(viewableFields, 'project_start_date') &&
                  dateDetailItem(
                    tableColumns.project_start_date,
                    project.project_start_date as string,
                    getFieldHistory('project_start_date'),
                  )}
                {canViewField(viewableFields, 'project_end_date') &&
                  dateDetailItem(
                    tableColumns.project_end_date,
                    project.project_end_date as string,
                    getFieldHistory('project_end_date'),
                  )}
                {canViewField(viewableFields, 'project_duration') &&
                  numberDetailItem(
                    tableColumns.project_duration,
                    project.project_duration as string,
                    'number',
                    getFieldHistory('project_duration'),
                  )}
              </div>
            </div>
            {canViewField(
              viewableFields,
              'blanket_or_individual_consideration',
            ) &&
              detailItem(
                tableColumns.blanket_or_individual_consideration,
                consideration?.name ?? '-',
                {
                  fieldHistory: getFieldHistory(
                    'blanket_or_individual_consideration',
                  ),
                },
              )}
          </div>
        </>
      )}
    </>
  )
}

export default ProjectCrossCutting
